'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ArrowLeft, CheckCircle, MapPin, BookOpen, DollarSign, Rocket, Globe, Users, Zap, Star, ChevronRight, Package, CreditCard, Image, Send, Check,  } from 'lucide-react';
import { FLORIDA_REGIONS, PUERTO_RICO_REGIONS } from '@/lib/catalogData';
import Icon from '@/components/ui/AppIcon';


// ── Types ─────────────────────────────────────────────────────────────────────

type Market = 'florida' | 'puerto-rico';

interface WelcomeState {
  selectedMarket: Market | null;
  selectedRegions: string[];
}

// ── Step data ─────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 0, label: 'Overview', icon: Rocket },
  { id: 1, label: 'Your Region', icon: MapPin },
  { id: 2, label: 'Pricing', icon: DollarSign },
  { id: 3, label: 'First Booking', icon: BookOpen },
];

const PLATFORM_STATS = [
  { value: '1,000+', label: 'Households per region', icon: Users },
  { value: '5 weeks', label: 'Distribution cycle', icon: Zap },
  { value: '2 markets', label: 'Florida & Puerto Rico', icon: Globe },
  { value: '5 formats', label: 'Ad placement types', icon: Package },
];

const PLACEMENT_TYPES = [
  { name: 'Front Cover', tag: 'Premium', price: '$1,500/issue', desc: 'Maximum visibility — the first thing readers see.', color: 'from-amber-500/20 to-amber-600/10 border-amber-500/30', badge: 'bg-amber-500/20 text-amber-300' },
  { name: 'Inside Front Cover', tag: 'Premium', price: '$1,200/issue', desc: 'High-impact position facing the front cover.', color: 'from-violet-500/20 to-violet-600/10 border-violet-500/30', badge: 'bg-violet-500/20 text-violet-300' },
  { name: 'Back Cover', tag: 'Premium', price: '$1,200/issue', desc: 'Second most viewed position in the book.', color: 'from-sky-500/20 to-sky-600/10 border-sky-500/30', badge: 'bg-sky-500/20 text-sky-300' },
  { name: 'Inside Back Cover', tag: 'Standard', price: '$600/issue', desc: 'Great visibility at a competitive rate.', color: 'from-brand-500/20 to-brand-600/10 border-brand-500/30', badge: 'bg-brand-500/20 text-brand-300' },
  { name: 'Standard Inside Page', tag: 'Standard', price: '$300/issue', desc: 'Full-page interior. 9 slots per region.', color: 'from-slate-500/20 to-slate-600/10 border-slate-500/30', badge: 'bg-slate-500/20 text-slate-300' },
];

const BOOKING_STEPS = [
  { step: 1, icon: Globe, title: 'Choose Market & Issue', desc: 'Select Florida or Puerto Rico and pick the upcoming issue you want your ad to appear in.' },
  { step: 2, icon: MapPin, title: 'Pick Your Regions', desc: 'Choose one or more zip code regions. Each region reaches 1,000 local households.' },
  { step: 3, icon: Package, title: 'Select Placement', desc: 'Pick your ad format — from a full front cover to a standard interior page.' },
  { step: 4, icon: Image, title: 'Upload Artwork', desc: 'Upload your print-ready artwork or request our design team to create it for you.' },
  { step: 5, icon: CreditCard, title: 'Checkout & Pay', desc: 'Secure payment via Stripe. Receive instant confirmation and order tracking.' },
  { step: 6, icon: Send, title: 'We Handle the Rest', desc: 'We print and distribute your ad to every household in your selected regions.' },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {STEPS.map((step, idx) => {
        const Icon = step.icon;
        const isActive = idx === current;
        const isDone = idx < current;
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isDone
                    ? 'bg-green-500/20 border border-green-500/50'
                    : isActive
                    ? 'bg-gradient-primary shadow-lg shadow-brand-500/30'
                    : 'bg-white/5 border border-white/10'
                }`}
              >
                {isDone ? (
                  <Check size={16} className="text-green-400" />
                ) : (
                  <Icon size={16} className={isActive ? 'text-white' : 'text-slate-500'} />
                )}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-brand-300' : isDone ? 'text-green-400' : 'text-slate-600'}`}>
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`w-12 sm:w-20 h-px mx-1 mb-5 transition-all duration-300 ${idx < current ? 'bg-green-500/50' : 'bg-white/10'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Step 0: Platform Overview ──────────────────────────────────────────────────

function StepOverview({ onNext }: { onNext: () => void }) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-300 text-xs font-semibold uppercase tracking-widest mb-5">
          <Star size={12} />
          Welcome to ZipAdBook
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-800 text-white mb-4">
          Hyperlocal advertising,{' '}
          <span className="text-gradient">delivered to doors</span>
        </h2>
        <p className="text-slate-400 max-w-xl mx-auto text-base leading-relaxed">
          ZipAdBook puts your business in front of 1,000+ local households every 5 weeks — printed, distributed, and impossible to ignore.
        </p>
      </div>

      {/* Stats bento */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {PLATFORM_STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="glass-card rounded-2xl p-5 text-center border border-white/5 hover:border-brand-500/20 transition-all">
              <Icon size={20} className="text-brand-400 mx-auto mb-2" />
              <div className="text-2xl font-800 text-white mb-1">{stat.value}</div>
              <div className="text-xs text-slate-500">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* How it works visual */}
      <div className="glass-card rounded-2xl p-6 border border-white/5 mb-8">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-5">How it works</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { num: '01', title: 'Book your placement', desc: 'Choose your region, format, and issue date online in minutes.' },
            { num: '02', title: 'Submit your artwork', desc: 'Upload print-ready files or request our design team.' },
            { num: '03', title: 'Reach local households', desc: 'We print and distribute to 1,000+ homes in your zip code regions.' },
          ].map((item) => (
            <div key={item.num} className="flex gap-4">
              <span className="text-3xl font-800 text-brand-500/30 leading-none shrink-0">{item.num}</span>
              <div>
                <p className="text-sm font-semibold text-white mb-1">{item.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={onNext}
          className="flex items-center gap-2 px-8 py-3 text-sm font-semibold btn-primary"
        >
          Get Started
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Step 1: Region Selection ───────────────────────────────────────────────────

function StepRegions({
  state,
  setState,
  onNext,
  onBack,
}: {
  state: WelcomeState;
  setState: React.Dispatch<React.SetStateAction<WelcomeState>>;
  onNext: () => void;
  onBack: () => void;
}) {
  const regions = state.selectedMarket === 'puerto-rico' ? PUERTO_RICO_REGIONS : FLORIDA_REGIONS;

  const toggleRegion = (id: string) => {
    setState(prev => ({
      ...prev,
      selectedRegions: prev.selectedRegions.includes(id)
        ? prev.selectedRegions.filter(r => r !== id)
        : [...prev.selectedRegions, id],
    }));
  };

  const setMarket = (market: Market) => {
    setState(prev => ({ ...prev, selectedMarket: market, selectedRegions: [] }));
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-2">Step 2 of 4</p>
        <h2 className="font-display text-2xl sm:text-3xl font-800 text-white mb-2">
          Where do your customers live?
        </h2>
        <p className="text-slate-400 text-sm">
          Select your target market and the regions you want to reach. Each region covers 1,000 households.
        </p>
      </div>

      {/* Market toggle */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-sm text-slate-400 font-medium">Market:</span>
        <div className="flex p-1 glass-card rounded-xl">
          <button
            onClick={() => setMarket('florida')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${state.selectedMarket === 'florida' ? 'bg-gradient-primary text-white' : 'text-slate-400 hover:text-white'}`}
          >
            🌴 Florida
          </button>
          <button
            onClick={() => setMarket('puerto-rico')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${state.selectedMarket === 'puerto-rico' ? 'bg-gradient-primary text-white' : 'text-slate-400 hover:text-white'}`}
          >
            🇵🇷 Puerto Rico
          </button>
        </div>
      </div>

      {/* Region grid */}
      <div className="grid sm:grid-cols-2 gap-2 mb-6 max-h-72 overflow-y-auto pr-1">
        {regions.map((region) => {
          const selected = state.selectedRegions.includes(region.id);
          return (
            <button
              key={region.id}
              onClick={() => toggleRegion(region.id)}
              className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all duration-200 ${
                selected
                  ? 'border-brand-500/60 bg-brand-500/10' :'border-white/8 bg-white/3 hover:border-white/20 hover:bg-white/5'
              }`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${selected ? 'border-brand-400 bg-brand-500' : 'border-slate-600'}`}>
                {selected && <Check size={10} className="text-white" />}
              </div>
              <div>
                <p className={`text-sm font-semibold ${selected ? 'text-white' : 'text-slate-300'}`}>{region.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{region.cities.slice(0, 3).join(', ')}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Selection summary */}
      {state.selectedRegions.length > 0 && (
        <div className="glass-card rounded-xl p-4 border border-brand-500/20 mb-6 flex items-center gap-3">
          <CheckCircle size={18} className="text-brand-400 shrink-0" />
          <p className="text-sm text-slate-300">
            <span className="text-white font-semibold">{state.selectedRegions.length} region{state.selectedRegions.length > 1 ? 's' : ''}</span> selected —{' '}
            reaching up to <span className="text-brand-300 font-semibold">{(state.selectedRegions.length * 1000).toLocaleString()} households</span>
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={15} />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!state.selectedMarket || state.selectedRegions.length === 0}
          className="flex items-center gap-2 px-7 py-2.5 text-sm font-semibold btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Pricing Explanation ────────────────────────────────────────────────

function StepPricing({
  state,
  onNext,
  onBack,
}: {
  state: WelcomeState;
  onNext: () => void;
  onBack: () => void;
}) {
  const regionCount = state.selectedRegions.length;

  const getDiscount = (count: number) => {
    if (count >= 8) return 15;
    if (count >= 5) return 10;
    if (count >= 3) return 5;
    return 0;
  };

  const discount = getDiscount(regionCount);

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-2">Step 3 of 4</p>
        <h2 className="font-display text-2xl sm:text-3xl font-800 text-white mb-2">
          Transparent, flat-rate pricing
        </h2>
        <p className="text-slate-400 text-sm">
          No hidden fees. Pay per issue, per region. Discounts apply when you book multiple regions.
        </p>
      </div>

      {/* Placement cards */}
      <div className="space-y-3 mb-6">
        {PLACEMENT_TYPES.map((p) => (
          <div key={p.name} className={`flex items-center justify-between p-4 rounded-xl border bg-gradient-to-r ${p.color} transition-all`}>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.badge}`}>{p.tag}</span>
              <div>
                <p className="text-sm font-semibold text-white">{p.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{p.desc}</p>
              </div>
            </div>
            <div className="text-right shrink-0 ml-4">
              <p className="text-sm font-700 text-white">{p.price}</p>
              <p className="text-xs text-slate-500">per region</p>
            </div>
          </div>
        ))}
      </div>

      {/* Multi-region discount callout */}
      <div className="glass-card rounded-2xl p-5 border border-white/5 mb-6">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4">Multi-region discounts (standard placements)</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: '1–2 regions', pct: 'Base rate' },
            { label: '3+ regions', pct: '5% off' },
            { label: '5+ regions', pct: '10% off' },
            { label: '8+ regions', pct: '15% off' },
          ].map((tier) => (
            <div key={tier.label} className="text-center p-3 rounded-xl bg-white/3 border border-white/5">
              <p className="text-sm font-700 text-brand-300">{tier.pct}</p>
              <p className="text-xs text-slate-500 mt-1">{tier.label}</p>
            </div>
          ))}
        </div>
        {discount > 0 && (
          <div className="mt-4 flex items-center gap-2 text-sm text-green-300 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-2.5">
            <CheckCircle size={15} />
            With your {regionCount} selected regions, you qualify for a <strong>{discount}% discount</strong> on standard placements.
          </div>
        )}
      </div>

      {/* 3-issue discount */}
      <div className="glass-card rounded-xl p-4 border border-brand-500/20 mb-6 flex items-start gap-3">
        <Star size={16} className="text-brand-400 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-300">
          <span className="text-white font-semibold">3-issue commitment discount:</span> Book 3 consecutive issues and save up to 33% on standard placements (from $300 to $200/issue per region).
        </p>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={15} />
          Back
        </button>
        <button
          onClick={onNext}
          className="flex items-center gap-2 px-7 py-2.5 text-sm font-semibold btn-primary"
        >
          Continue
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

// ── Step 3: First Booking Walkthrough ─────────────────────────────────────────

function StepBookingWalkthrough({
  state,
  onBack,
}: {
  state: WelcomeState;
  onBack: () => void;
}) {
  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-2">Step 4 of 4</p>
        <h2 className="font-display text-2xl sm:text-3xl font-800 text-white mb-2">
          Your first booking, step by step
        </h2>
        <p className="text-slate-400 text-sm">
          Here's exactly what happens when you click "Book an Ad" — no surprises.
        </p>
      </div>

      {/* Booking steps */}
      <div className="space-y-3 mb-8">
        {BOOKING_STEPS.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={item.step} className="flex items-start gap-4 p-4 glass-card rounded-xl border border-white/5 hover:border-brand-500/20 transition-all">
              <div className="w-9 h-9 rounded-xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center shrink-0">
                <Icon size={16} className="text-brand-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-700 text-brand-500/60">0{item.step}</span>
                  <p className="text-sm font-semibold text-white">{item.title}</p>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
              {idx < BOOKING_STEPS.length - 1 && (
                <ChevronRight size={14} className="text-slate-700 shrink-0 mt-2" />
              )}
            </div>
          );
        })}
      </div>

      {/* Summary of selections */}
      {state.selectedRegions.length > 0 && (
        <div className="glass-card rounded-2xl p-5 border border-brand-500/20 mb-8">
          <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-3">Your selections so far</p>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-xs text-brand-300 font-medium">
              {state.selectedMarket === 'florida' ? '🌴 Florida' : '🇵🇷 Puerto Rico'}
            </span>
            <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300 font-medium">
              {state.selectedRegions.length} region{state.selectedRegions.length > 1 ? 's' : ''} selected
            </span>
            <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300 font-medium">
              {(state.selectedRegions.length * 1000).toLocaleString()} households
            </span>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={15} />
          Back
        </button>
        <Link
          href="/order-wizard"
          className="flex items-center justify-center gap-2 flex-1 sm:flex-none px-8 py-3 text-sm font-semibold btn-primary"
        >
          <Rocket size={16} />
          Book My First Ad
        </Link>
        <Link
          href="/regions-pricing"
          className="flex items-center justify-center gap-2 flex-1 sm:flex-none px-6 py-3 text-sm font-medium btn-outline"
        >
          Explore Pricing
          <ArrowRight size={15} />
        </Link>
      </div>

      <p className="text-center text-xs text-slate-600 mt-5">
        Questions? Visit our{' '}
        <Link href="/help" className="text-brand-400 hover:text-brand-300 transition-colors">Help Center</Link>
        {' '}or call{' '}
        <a href="tel:+13212653177" className="text-brand-400 hover:text-brand-300 transition-colors">+1 (321) 265-3177</a>
      </p>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function WelcomeFlowContent() {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WelcomeState>({
    selectedMarket: 'florida',
    selectedRegions: [],
  });

  const next = () => setStep(s => Math.min(s + 1, 3));
  const back = () => setStep(s => Math.max(s - 1, 0));

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Advertiser Onboarding</p>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} />

        {/* Card */}
        <div className="glass-card rounded-3xl border border-white/8 p-6 sm:p-10 shadow-2xl shadow-black/40">
          {step === 0 && <StepOverview onNext={next} />}
          {step === 1 && <StepRegions state={state} setState={setState} onNext={next} onBack={back} />}
          {step === 2 && <StepPricing state={state} onNext={next} onBack={back} />}
          {step === 3 && <StepBookingWalkthrough state={state} onBack={back} />}
        </div>

        {/* Skip link */}
        {step < 3 && (
          <div className="text-center mt-5">
            <Link href="/order-wizard" className="text-xs text-slate-600 hover:text-slate-400 transition-colors">
              Skip intro and book now →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
